// apps/web/src/features/mypage/pages/MyPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePageScale } from "@/ui/usePageScale";
import { PageShell } from "@/ui/PageShell";
import { withBase } from "@/ui/Header";
import { libraryList } from "@/lib/api";
import { getDeviceModel, SPC_MODEL } from "@/lib/androidBridge";
import { COLOR, FW, BORDER } from "@/ui/tokens";

const API_BASE  = (import.meta.env.VITE_API_BASE  ?? "").replace(/\/+$/, "");
const AUTH_BASE = (import.meta.env.VITE_AUTH_BASE ?? import.meta.env.VITE_API_BASE ?? "").replace(/\/+$/, "");

const BASE = {
    W: 1264,
    MARGIN_X: 111,

    // 1264x1424 기준 하단 여백이 과하게 남는 이슈가 있어 상단/중간 간격을 더 타이트하게 조정
    TOP_GAP: 10,
    AVATAR: 350,          // (기존 240) 요구사항
    AVATAR_RING: 18,

    NAME_FZ: 45,
    ID_FZ: 35,

    EDIT_BTN_W: 260,
    EDIT_BTN_H: 72,
    EDIT_BTN_FZ: 30,

    STAT_ROW_GAP: 50,
    STAT_BOX_H: 220,
    STAT_BOX_RADIUS: 30,
    STAT_TITLE_FZ: 35,
    STAT_VALUE_FZ: 35,
    STAT_ICON: 40,

    DESC_FZ: 38,
    DESC_GAP_TOP: 74,

    LIST_RADIUS: 30,
    LIST_ROW_H: 150,
    LIST_ICON: 54,
    LIST_TXT_FZ: 35,
    CHEV: 55,

    LIST_GAP_TOP: 30,
    BOTTOM_PAD: 0,
} as const;


function useScale() {
    const { s, px, sf, sfpx } = usePageScale({ baseW: BASE.W, max: 1.2 });
    return { s, spx: px, sf, sfpx };
}

type MeUser = {
    id: number;
    username: string;
    nickname: string | null;
    avatar_url: string | null;
    created_at: string;
};

export default function MyPage() {
    const nav = useNavigate();
    const { s, spx, sfpx } = useScale();
    const isSpc = getDeviceModel() === SPC_MODEL;

    const [me, setMe] = useState<MeUser | null>(null);
    const [stats, setStats] = useState<{ my_reviews: number; hearts_total: number } | null>(null);
    const [libraryTotal, setLibraryTotal] = useState<number>(0);

    const access = localStorage.getItem("bt.access");

    // 화면 진입 시 /me, /me/stats 로드
    useEffect(() => {
        if (!access) return;

        const headers: Record<string, string> = {
            Accept: "application/json",
            Authorization: `Bearer ${access}`,
        };

        (async () => {
            try {
                const [r1, r2, libData] = await Promise.all([
                    fetch(`${AUTH_BASE}/me`, { headers }),
                    fetch(`${API_BASE}/me/stats`, { headers }),
                    libraryList({ status: 'ALL', page: 1, size: 1 }),
                ]);

                if (r1.ok) {
                    const d1 = await r1.json();
                    setMe(d1.user || null);
                }
                if (r2.ok) {
                    const d2 = await r2.json();
                    setStats({ my_reviews: Number(d2.my_reviews || 0), hearts_total: Number(d2.hearts_total || 0) });
                }
                setLibraryTotal(Number(libData?.total || 0));
            } catch {
                // 로컬 개발 단계에서는 조용히 실패 처리
            }
        })();
    }, [access]);

    const avatarSrc = useMemo(() => {
        const raw = me?.avatar_url || "";
        if (raw) {
            if (raw.startsWith("http")) return raw;
            return `${API_BASE}${raw}`;
        }
        return withBase("icons/profile_default.svg");
    }, [me?.avatar_url]);

    // 로그인 가드: (사용자 요구) 별도 페이지로 안내
    if (!access) {
        // RequireLogin 컴포넌트로 감쌀 예정이지만, 혹시 단독 렌더될 때도 안전하게 처리
        nav("/mypage/need-login", { replace: true });
        return null;
    }

    const nickname = me?.nickname || "-";
    const username = me?.username || "-";
    const hearts = stats?.hearts_total ?? 0;
    const myReviews = stats?.my_reviews ?? 0;

    const IconBook = withBase("icons/library_on.svg");
    const IconHeart = withBase("icons/heart_red.svg"); // 교체 가능
    const IconPen = withBase("icons/review.svg"); // 교체 가능
    const IconPageBox = withBase("icons/PageBox_logo.webp"); // 제공된 아이콘 파일명으로 교체
    const IconSpace = withBase("icons/SPACE_library_icon.webp"); // 제공된 아이콘 파일명으로 교체
    const IconChev = withBase("icons/next1.svg"); // 없으면 svg 하나 추가

    const StatBox = ({
                         title,
                         icon,
                         value,
                     }: {
        title: string;
        icon: string;
        value: number;
    }) => (
        <div
            style={{
                height: spx(BASE.STAT_BOX_H),
                borderRadius: s(BASE.STAT_BOX_RADIUS),
                border: BORDER.boldDark,
                background: COLOR.white,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <div style={{ fontSize: sfpx(BASE.STAT_TITLE_FZ), fontWeight: FW.extraBold, color: COLOR.text, lineHeight: 1.1 }}>
              {title}
            </div>
            <div style={{ height: spx(20) }} />
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: spx(12) }}>
                <img src={icon} alt="" style={{ width: spx(BASE.STAT_ICON), height: spx(BASE.STAT_ICON), display: "block" }} />
                <div style={{ fontSize: sfpx(BASE.STAT_VALUE_FZ), fontWeight: 900, color: COLOR.text }}>{value}</div>
            </div>
        </div>
    );

    const Row = ({
                     icon,
                     text,
                     onClick,
                 }: {
        icon: string;
        text: string;
        onClick: () => void;
    }) => (
        <button
            type="button"
            onClick={onClick}
            style={{
                width: "100%",
                height: spx(BASE.LIST_ROW_H),
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: `0 ${spx(28)}`,
                background: "transparent",
                border: "none",
                cursor: "pointer",
            }}
        >
            <div style={{ display: "flex", alignItems: "center", gap: spx(18) }}>
                <img src={icon} alt="" style={{ width: spx(BASE.LIST_ICON), height: spx(BASE.LIST_ICON), display: "block" }} />
                <div style={{ fontSize: sfpx(BASE.LIST_TXT_FZ), fontWeight: FW.extraBold, color: COLOR.text }}>{text}</div>
            </div>
            <img src={IconChev} alt="" style={{ width: spx(BASE.CHEV), height: spx(BASE.CHEV), display: "block" }} />
        </button>
    );

    const onLogout = () => {
        localStorage.removeItem("bt.access");
        localStorage.removeItem("bt.refresh");
        localStorage.removeItem("bt.user");
        nav("/auth/login", { replace: true });
    };

    return (
        <PageShell
            title="마이페이지"
            width={spx(BASE.W)}
            headerRight={
                <button
                    type="button"
                    onClick={onLogout}
                    style={{
                        height: `${s(64) + (isSpc ? 20 : 0)}px`,
                        padding: `0 ${s(18) + (isSpc ? 14 : 0)}px`,
                        borderRadius: s(18),
                        background: COLOR.black,
                        color: COLOR.white,
                        fontSize: `${s(24) + (isSpc ? 5 : 0)}px`,
                        fontWeight: 900,
                        border: "none",
                    }}
                >
                    로그아웃
                </button>
            }
            bodyStyle={{ paddingLeft: spx(BASE.MARGIN_X), paddingRight: spx(BASE.MARGIN_X) }}
        >

            <div style={{ height: spx(BASE.TOP_GAP) }} />

            {/* 상단 프로필 */}
            <div style={{ display: "flex", alignItems: "flex-start", gap: spx(30) }}>
                <img
                    src={avatarSrc}
                    alt=""
                    style={{
                        width: spx(BASE.AVATAR),
                        height: spx(BASE.AVATAR),
                        borderRadius: "999px",
                        objectFit: "cover",
                        display: "block",
                        background: "#cfe6d5",
                    }}
                />

                <div style={{ paddingTop: spx(14) }}>
                    <div style={{ fontSize: sfpx(BASE.NAME_FZ), fontWeight: 900, color: COLOR.text }}>{nickname}</div>
                    <div style={{ height: spx(12) }} />
                    <div style={{ fontSize: sfpx(BASE.ID_FZ), fontWeight: FW.bold, color: COLOR.text }}>{username}</div>

                    {/* 내 정보 수정 버튼 위아래 위치 */}
                    <div style={{ height: spx(58) }} />
                    <button
                        type="button"
                        onClick={() => nav("/mypage/edit")}
                        style={{
                            width: spx(BASE.EDIT_BTN_W),
                            height: spx(BASE.EDIT_BTN_H),
                            borderRadius: s(20),
                            background: "#9b9b9b",
                            border: "none",
                            color: COLOR.white,
                            fontSize: sfpx(BASE.EDIT_BTN_FZ),
                            fontWeight: 900,
                        }}
                    >
                        내 정보 수정
                    </button>
                </div>
            </div>

            <div style={{ height: spx(BASE.STAT_ROW_GAP) }} />

            {/* 통계 카드 3개 */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spx(22) }}>
                <StatBox title="내 서재" icon={IconBook} value={libraryTotal} />
                {/*<StatBox title="보유 하트" icon={IconHeart} value={hearts} />*/}
                <StatBox title="나의 리뷰" icon={IconPen} value={myReviews} />

            </div>

            <div style={{ height: spx(BASE.DESC_GAP_TOP) }} />
            <div style={{ fontSize: sfpx(BASE.DESC_FZ), fontWeight: FW.extraBold, color: COLOR.text, marginLeft: spx(14), }}>
                책 읽는 즐거움, 당신을 위한 무료 책 쉼터
            </div>

            <div style={{ height: spx(BASE.LIST_GAP_TOP) }} />

            {/* 메뉴 박스 */}
            <div
                style={{
                    width: "100%",
                    borderRadius: s(BASE.LIST_RADIUS),
                    border: BORDER.boldDark,
                    background: COLOR.white,
                    overflow: "hidden",
                }}
            >
                <Row
                    icon={IconPageBox}
                    text="페이지 박스"
                    onClick={() => {
                        // 가드 없이 호출 (요구사항)
                        // @ts-ignore
                        window.Android.callPageBox();
                    }}
                />
                <div style={{ height: 1, background: "#e3e3e3" }} />
            </div>

            <div style={{height: spx(BASE.BOTTOM_PAD)}}/>

        </PageShell>
    );
}
